import {
  MAX_SKILL_LEVEL,
  SKILLS,
  SKILL_KEYS,
  isRusting,
  skillLevel,
  skillProgress,
  skillXpAt,
  type Progress,
  type SkillKey,
} from '@london-life/contracts';

import { useCatalogue } from '../lib/content';

// skills grow from what the character does, make those things go better, and rust when left unused
export function SkillsTab({ progress }: { progress: Progress }) {
  const now = new Date();
  // where each skill can be trained, from the activities that teach it (published content)
  const activities = useCatalogue()?.activities ?? [];
  const TRAINED_BY = Object.fromEntries(
    SKILL_KEYS.map((skill) => [
      skill,
      activities.filter((activity) => activity.skills?.[skill]).map((activity) => activity.label),
    ]),
  ) as Record<SkillKey, string[]>;
  return (
    <div className="profile-grid">
      <section className="profile-card profile-wide" aria-labelledby="skills-title">
        <h3 id="skills-title">Skills</h3>
        <p className="profile-hint">
          Every skill grows from doing things, and each level makes those things go 3% better. Leave a skill for more
          than 3 days and it starts to rust.
        </p>
        <ul className="skills-list">
          {SKILL_KEYS.map((key) => {
            const saved = progress.skills[key];
            const xp = saved ? skillXpAt(saved.xp, saved.lastTrainedAt, now) : 0;
            const level = skillLevel(xp);
            const rusting = saved ? isRusting(saved.lastTrainedAt, now) : false;
            return (
              <li key={key}>
                <em aria-hidden="true">{SKILLS[key].icon}</em>
                <span className="skills-name">
                  <strong>
                    {SKILLS[key].label} <span className="skills-level">Level {level}</span>
                    {rusting && <span className="profile-badge warn">Rusting</span>}
                  </strong>
                  <small>
                    {TRAINED_BY[key].length ? `Train: ${TRAINED_BY[key].join(', ')}` : SKILLS[key].description}
                  </small>
                </span>
                <span
                  className="needs-bar"
                  role="meter"
                  aria-label={`${SKILLS[key].label} level ${level} of ${MAX_SKILL_LEVEL}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(skillProgress(xp) * 100)}
                >
                  <b style={{ width: `${skillProgress(xp) * 100}%`, background: 'var(--ll-info)' }} />
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
